import React, { useState, useEffect, useCallback } from 'react';
import { useTutorial } from '../context/TutorialContext';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiCheck, FiTrash2, FiMessageSquare, FiCheckSquare, FiArrowLeft } from 'react-icons/fi';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { apiFetch } from '../utils/api';
import { toast } from '../utils/customToast';
import DarkSkeleton from '../components/shared/DarkSkeleton';

import { sanitizeHtml } from '../utils/sanitizeHtml';
import usePageTitle from '../hooks/usePageTitle';

const apiBase = process.env.REACT_APP_API_URL || '/api';

const Notifications = () => {
    usePageTitle('Notifications');
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const { getAuthHeaders, activeTenantId } = useAuth();
    const { handleApiError } = useApiErrorHandler();
    const navigate = useNavigate();
    const { notifyAction: tutorialNotify } = useTutorial();

    useEffect(() => {
        tutorialNotify('visited-notifications');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchNotifications = useCallback(async () => {
        if (!activeTenantId) {
            setLoading(false);
            setNotifications([]);
            return;
        }
        
        setLoading(true);
        try {
            const data = await apiFetch(
                `${apiBase}/notifications`,
                { headers: getAuthHeaders() },
                handleApiError
            );
            setNotifications(data);
        } catch (err) {
            console.error('Error fetching notifications:', err);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders, activeTenantId, handleApiError]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const markAsRead = async (notificationId) => {
        try {
            await apiFetch(
                `${apiBase}/notifications/${notificationId}/read`,
                {
                    method: 'PUT',
                    headers: getAuthHeaders()
                },
                handleApiError
            );
            
            setNotifications(prev => 
                prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
            );
        } catch (err) {
            console.error('Error marking as read:', err);
        }
    };

    const markAllAsRead = async () => {
        try {
            await apiFetch(
                `${apiBase}/notifications/mark-all-read`,
                {
                    method: 'PUT',
                    headers: getAuthHeaders()
                },
                handleApiError
            );
            
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            toast.success('Toutes les notifications marquées comme lues');
        } catch (err) {
            console.error('Error marking all as read:', err);
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            await fetch(`${apiBase}/notifications/${notificationId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            
            setNotifications(prev => prev.filter(n => n._id !== notificationId));
            toast.success('Notification supprimée');
        } catch (err) {
            console.error('Error deleting notification:', err);
        }
    };

    const handleNotificationClick = async (notification) => {
        await markAsRead(notification._id);
        
        if (notification.referenceType === 'message') {
            // Rediriger vers la page messages avec l'ID du message
            navigate(`/messages?openMsg=${notification.referenceId}`);
        } else if (notification.referenceType === 'todo') {
            // Stocker l'ID du todo à ouvrir dans sessionStorage
            sessionStorage.setItem('openTodoId', notification.referenceId);
            // Rediriger vers la page messages
            navigate('/messages');
        }
    };

    const getNotificationIcon = (type) => {
        if (type === 'mention_message') return <FiMessageSquare className="h-5 w-5" aria-hidden="true" />;
        if (type === 'mention_todo') return <FiCheckSquare className="h-5 w-5" aria-hidden="true" />;
        return <FiBell className="h-5 w-5" aria-hidden="true" />;
    };

    const getNotificationText = (notification) => {
        if (notification.type === 'mention_message') {
            return `${notification.senderName} vous a mentionné dans un message`;
        }
        if (notification.type === 'mention_todo') {
            return `${notification.senderName} vous a mentionné dans une tâche`;
        }
        return 'Nouvelle notification';
    };

    const stripHtml = (html) => {
            const tempDiv = document.createElement('div');
            // sanitize incoming HTML first
            const sanitized = sanitizeHtml(html || '');

            // Nettoyer les mentions d'utilisateurs avant d'extraire le texte
            // Remplacer les mentions @user:id[Nom] par juste le nom avec un @ devant
            let content = sanitized.replace(/@user:([A-Za-z0-9_-]+)\[([^\]]+)\]/g, '@$2');

            // Ré-sanitize after we modified the string, then set into the DOM
            tempDiv.innerHTML = sanitizeHtml(content);
            const userMentions = tempDiv.querySelectorAll('.user-mention');
            userMentions.forEach(el => {
                el.textContent = '@' + el.textContent;
            });

            return tempDiv.textContent || tempDiv.innerText || '';
        };

    const unreadCount = notifications.filter(n => !n.read).length;

    if (!activeTenantId) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
                <Header />
                <main className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-4">
                        <div className="text-6xl mb-4">🔔</div>
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
                            Aucune organisation
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Pour accéder aux notifications, créez une organisation ou rejoignez-en une existante.
                        </p>
                        <button
                            onClick={() => navigate('/join-tenant')}
                            className="bg-primary text-white dark:text-black font-semibold py-3 px-6 rounded hover:bg-primary-hover transition"
                        >
                            Créer ou rejoindre une organisation
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
                <Header />
                <main className="flex-1 container mx-auto px-4 pt-8">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <DarkSkeleton height={40} className="mb-4" />
                        {[1, 2, 3].map(i => (
                            <div key={i} className="mb-4">
                                <DarkSkeleton height={80} />
                            </div>
                        ))}
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
            <Header />
            <main className="flex-1 pb-8">
                <div className="container mx-auto px-4 pt-4 sm:pt-8">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/messages')}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 sm:mb-6"
                    >
                        <FiArrowLeft aria-hidden="true" /> Retour aux messages
                    </button>

                    <div className="p-6 border-b dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-black dark:text-white flex items-center gap-2">
                                    <FiBell className="h-6 w-6" aria-hidden="true" />
                                    Notifications
                                </h1>
                                {unreadCount > 0 && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        {unreadCount} notification{unreadCount > 1 ? 's' : ''} non lue{unreadCount > 1 ? 's' : ''}
                                    </p>
                                )}
                            </div>
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="flex items-center gap-2 text-sm bg-primary text-white dark:text-black px-4 py-2 rounded hover:bg-primary-hover transition"
                                >
                                    <FiCheck className="h-4 w-4" aria-hidden="true" />
                                    Tout marquer comme lu
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="divide-y dark:divide-gray-700">
                        {notifications.length === 0 ? (
                            <div className="p-12 text-center">
                                <FiBell className="h-16 w-16 text-gray-400 mx-auto mb-4" aria-hidden="true" />
                                <p className="text-gray-600 dark:text-gray-400">
                                    Aucune notification
                                </p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification._id}
                                    className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer ${
                                        !notification.read ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                                    }`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`p-2 rounded-lg ${
                                            notification.type === 'mention_message' 
                                                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                                : 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                                        }`}>
                                            {getNotificationIcon(notification.type)}
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-black dark:text-white">
                                                {getNotificationText(notification)}
                                            </p>
                                            {notification.content && (
                                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                                    {stripHtml(notification.content)}
                                                </p>
                                            )}
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                                                {new Date(notification.createdAt).toLocaleString('fr-FR')}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {!notification.read && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        markAsRead(notification._id);
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition"
                                                    title="Marquer comme lu"
                                                    aria-label="Marquer comme lu"
                                                >
                                                    <FiCheck className="h-5 w-5" aria-hidden="true" />
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteNotification(notification._id);
                                                }}
                                                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition"
                                                title="Supprimer"
                                                aria-label="Supprimer la notification"
                                            >
                                                <FiTrash2 className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </main>



            <Footer />
        </div>
    );
};

export default Notifications;